import React, { useState } from 'react';
import { Box, Typography, Button, Chip, IconButton, Tooltip, TextField } from '@mui/material';
import CloudDoneIcon from '@mui/icons-material/CloudDone';
import VisibilityIcon from '@mui/icons-material/Visibility';

const RestorableUploadRow = ({ 
  label, 
  file, 
  fileLink, 
  handler, 
  isOptional = false, 
  onPreview,
  fileName = null,
  tanValue = '',
  onTanChange
}) => {
  const displayLabel = isOptional ? `${label} (Optional)` : label;
  const hasRestoredFile = fileLink && !file;
  const [tanError, setTanError] = useState('');
  
  // Validation for TAN Number
  const validateTan = (value) => {
    const tanRegex = /^[A-Z]{4}[0-9]{5}[A-Z]{1}$/;
    if (!value) {
      setTanError('');
      return true;
    }
    if (!tanRegex.test(value)) {
      setTanError('TAN must be 10 characters (e.g., ABCD12345E)');
      return false;
    }
    setTanError('');
    return true;
  };

  const handleFileChange = (e) => {
    handler(e);
  };

  const handlePreview = () => {
    if (file && onPreview) {
      onPreview(label, file);
    } else if (fileLink && onPreview) {
      // For restored files, we'll preview using the fileLink
      onPreview(label, { isUrl: true, url: fileLink, name: fileName });
    }
  };

  const renderPreview = () => {
    // If we have a new file, show it
    if (file) {
      return file.type?.startsWith('image/') ? (
        <img
          src={URL.createObjectURL(file)}
          alt={label}
          style={{ width: '100%', height: '100%', objectFit: 'cover' }}
        />
      ) : (
        <Box 
          sx={{ 
            width: '100%', 
            height: '100%', 
            display: 'flex', 
            alignItems: 'center', 
            justifyContent: 'center',
            bgcolor: 'primary.50',
            color: 'primary.main'
          }}
        >
          <Typography variant="caption" align="center">
            PDF
          </Typography>
        </Box>
      );
    }
    
    // If we have a restored file link, show placeholder
    if (hasRestoredFile) {
      return (
        <Box 
          sx={{ 
            width: '100%', 
            height: '100%', 
            display: 'flex', 
            flexDirection: 'column',
            alignItems: 'center', 
            justifyContent: 'center',
            bgcolor: 'success.50',
            color: 'success.main',
            cursor: 'pointer'
          }}
          onClick={handlePreview}
        >
          <CloudDoneIcon sx={{ mb: 1 }} />
          <Typography variant="caption" align="center">
            Restored
          </Typography>
        </Box>
      );
    }

    // Empty state
    return (
      <Box sx={{ width: '100%', height: '100%', bgcolor: 'grey.100' }} />
    );
  };

  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
      <Box
        sx={{
          width: 100,
          height: 100,
          border: 1,
          borderColor: hasRestoredFile ? 'success.main' : file ? 'primary.main' : 'divider',
          borderRadius: 1,
          overflow: 'hidden',
          cursor: (file || hasRestoredFile) ? 'pointer' : 'default',
          position: 'relative'
        }}
        onClick={handlePreview}
      >
        {renderPreview()}
        {hasRestoredFile && (
          <Chip
            icon={<CloudDoneIcon />}
            label="Restored"
            size="small"
            color="success"
            sx={{
              position: 'absolute',
              top: 4,
              left: 4,
              fontSize: '0.6rem',
              height: 20
            }}
          />
        )}
      </Box>

      <Box sx={{ flex: 1 }}>
        <Typography variant="subtitle1" gutterBottom>
          <strong>{displayLabel}:</strong> {file?.name || fileName || ''}
        </Typography>
        
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          {/* Upload/Replace Button */}
          <Button 
            variant={file || hasRestoredFile ? 'text' : 'outlined'} 
            component="label" 
            sx={{ p: 0 }}
          >
            {file ? 'Replace' : hasRestoredFile ? 'Replace' : `Upload ${label}`}
            <input
              type="file"
              hidden
              accept={label === 'GST Certificate' ? 'application/pdf' : 'image/*,application/pdf'}
              onChange={handleFileChange}
            />
          </Button>

          {/* Preview Button for Restored Files */}
          {hasRestoredFile && (
            <Tooltip title="Preview restored document">
              <IconButton size="small" onClick={handlePreview} color="success">
                <VisibilityIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          )}
        </Box>

        {/* Status Messages */}
        {hasRestoredFile && (
          <Typography variant="caption" color="success.main" sx={{ display: 'block', mt: 0.5 }}>
            ✓ Document restored from previous session
          </Typography>
        )}

        {/* Show TAN number field for TAN Certificate - always show if no extracted value or if user wants to override */}
        {label.startsWith("TAN Certificate") && (
          <TextField
            label="Enter TAN Number (Required if extraction fails)"
            value={tanValue}
            onChange={(e) => {
              const val = e.target.value.toUpperCase();
              onTanChange?.(val);   // updates parent state
              validateTan(val);     // validation
            }}
            error={!!tanError}
            helperText={tanError || "TAN will be auto-extracted if possible, otherwise enter manually"}
            size="small"
            sx={{ mt: 2, width: "60%" }}
            inputProps={{ maxLength: 10 }}
            placeholder="ABCD12345E"
          />
        )}
      </Box>
    </Box>
  );
};

export default RestorableUploadRow;